import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from '@mui/material'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints, PAYMENT_METHODS } from '../../consts'
import InlineBankForm from './InlineBankForm'
import logger from '../../utils/logger'
import styles from './BillingSetupWizard.module.scss'

const COUNTRIES = [
  { code: 'CA', label: 'Canada', flag: '\uD83C\uDDE8\uD83C\uDDE6', currency: 'CAD' },
  { code: 'US', label: 'United States', flag: '\uD83C\uDDFA\uD83C\uDDF8', currency: 'USD' }
]

const BillingSetupWizard = ({ userOrganization, onComplete }) => {
  const [step, setStep] = useState(1)
  const [country, setCountry] = useState(null)
  const [selectedMethod, setSelectedMethod] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [createdProfile, setCreatedProfile] = useState(null)

  const availableMethods = country
    ? Object.entries(PAYMENT_METHODS).filter(([, cfg]) => cfg.countries.includes(country))
    : []

  const handleCountrySelect = (code) => {
    setCountry(code)
    setSelectedMethod(null)
    setError(null)
    setStep(2)
  }

  const [stripeChoice, setStripeChoice] = useState(null) // 'existing' | 'new'

  const handleMethodSelect = (methodKey) => {
    setSelectedMethod(methodKey)
    setError(null)
    setStripeChoice(null)

    if (methodKey === 'credit_card') {
      setStep('stripe_choice')
    }
  }

  const handleStripeChoice = async (choice) => {
    setStripeChoice(choice)
    await createProfile('credit_card', choice)
  }

  const createProfile = async (method, stripeSetupType) => {
    setSubmitting(true)
    setError(null)
    try {
      const response = await authenticatedFetchJson(apiEndpoints.billingSetup, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_name: userOrganization,
          country_code: country,
          preferred_payment_method: method,
          ...(stripeSetupType ? { stripe_setup_type: stripeSetupType } : {})
        })
      })
      setCreatedProfile(response.data)

      if (method === 'credit_card') {
        setStep(3)
      }
      return response.data
    } catch (err) {
      logger.error('Failed to create billing profile:', err)
      setError(err.message || 'Failed to set up billing. Please try again.')
      return null
    } finally {
      setSubmitting(false)
    }
  }

  const handleBankSetup = async () => {
    const profile = await createProfile(selectedMethod)
    if (profile) {
      setStep('bank_form')
    }
  }

  const handleCreditCardDone = () => {
    onComplete()
  }

  return (
    <div className={styles.wizard} data-testid='billing-setup-wizard'>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.iconCircle}>
          <FontAwesomeIcon icon='file-invoice-dollar' className={styles.headerIcon} />
        </div>
        <h2 className={styles.title}>Set Up Your Billing</h2>
        <p className={styles.subtitle}>
          Complete your billing profile to get started. This only takes a minute.
        </p>
      </div>

      {/* Progress */}
      <div className={styles.progress}>
        <div className={`${styles.progressStep} ${step >= 1 ? styles.progressStepActive : ''}`}>
          <span className={styles.progressDot} />
          <span className={styles.progressLabel}>Country</span>
        </div>
        <div className={styles.progressLine} />
        <div className={`${styles.progressStep} ${step >= 2 || step === 'stripe_choice' ? styles.progressStepActive : ''}`}>
          <span className={styles.progressDot} />
          <span className={styles.progressLabel}>Payment</span>
        </div>
        <div className={styles.progressLine} />
        <div className={`${styles.progressStep} ${step >= 3 || step === 'bank_form' ? styles.progressStepActive : ''}`}>
          <span className={styles.progressDot} />
          <span className={styles.progressLabel}>Done</span>
        </div>
      </div>

      {error && (
        <div className={styles.error} data-testid='setup-error'>
          {error}
        </div>
      )}

      {/* Step 1: Country */}
      {step === 1 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Where is your business located?</h3>
          <div className={styles.countryCards}>
            {COUNTRIES.map(c => (
              <button
                key={c.code}
                className={`${styles.countryCard} ${country === c.code ? styles.countryCardSelected : ''}`}
                onClick={() => handleCountrySelect(c.code)}
                data-testid={`country-${c.code}`}
              >
                <span className={styles.countryFlag}>{c.flag}</span>
                <span className={styles.countryName}>{c.label}</span>
                <span className={styles.countryCurrency}>{c.currency}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Payment Method */}
      {step === 2 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>How would you like to pay?</h3>
          <p className={styles.stepHint}>
            Select your preferred payment method. You can change this later.
          </p>
          <div className={styles.methodCards}>
            {availableMethods.map(([key, cfg]) => (
              <button
                key={key}
                className={`${styles.methodCard} ${selectedMethod === key ? styles.methodCardSelected : ''}`}
                onClick={() => handleMethodSelect(key)}
                disabled={submitting}
                data-testid={`method-${key}`}
              >
                <FontAwesomeIcon
                  icon={key === 'credit_card' ? 'credit-card' : 'university'}
                  className={styles.methodIcon}
                />
                <span className={styles.methodName}>{cfg.label}</span>
                <span className={styles.methodDesc}>
                  {key === 'credit_card' && 'Pay securely via Stripe'}
                  {key === 'pad' && 'Direct debit from your Canadian bank account'}
                  {key === 'ach' && 'Direct debit from your US bank account'}
                </span>
              </button>
            ))}
          </div>

          {selectedMethod && selectedMethod !== 'credit_card' && (
            <div className={styles.actionArea}>
              <Button
                variant='contained'
                onClick={handleBankSetup}
                disabled={submitting}
                className={styles.continueButton}
                data-testid='continue-bank-setup'
              >
                {submitting ? 'Setting up...' : 'Continue with Bank Details'}
              </Button>
            </div>
          )}

          <button
            className={styles.backLink}
            onClick={() => { setStep(1); setSelectedMethod(null) }}
            type='button'
          >
            <FontAwesomeIcon icon='arrow-left' /> Change country
          </button>
        </div>
      )}

      {/* Step 2b: Stripe — existing or new? */}
      {step === 'stripe_choice' && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Have you previously set up billing with us?</h3>
          <p className={styles.stepHint}>
            This helps us connect your account faster.
          </p>
          <div className={styles.countryCards}>
            <button
              className={`${styles.countryCard} ${stripeChoice === 'existing' ? styles.countryCardSelected : ''}`}
              onClick={() => handleStripeChoice('existing')}
              disabled={submitting}
              data-testid='stripe-existing'
            >
              <FontAwesomeIcon icon='link' className={styles.methodIcon} />
              <span className={styles.countryName}>Yes, existing account</span>
              <span className={styles.countryCurrency}>We&#39;ll connect your Stripe account automatically</span>
            </button>
            <button
              className={`${styles.countryCard} ${stripeChoice === 'new' ? styles.countryCardSelected : ''}`}
              onClick={() => handleStripeChoice('new')}
              disabled={submitting}
              data-testid='stripe-new'
            >
              <FontAwesomeIcon icon='plus-circle' className={styles.methodIcon} />
              <span className={styles.countryName}>No, new setup</span>
              <span className={styles.countryCurrency}>Your admin will create your billing account</span>
            </button>
          </div>
          {submitting && <p className={styles.stepHint}>Setting up your profile...</p>}
          <button
            className={styles.backLink}
            onClick={() => { setStep(2); setSelectedMethod(null); setStripeChoice(null) }}
            type='button'
            disabled={submitting}
          >
            <FontAwesomeIcon icon='arrow-left' /> Back to payment methods
          </button>
        </div>
      )}

      {/* Step 3: Done (Credit Card) */}
      {step === 3 && selectedMethod === 'credit_card' && (
        <div className={styles.stepContent}>
          <div className={styles.successCard}>
            <FontAwesomeIcon
              icon='check-circle'
              className={styles.successIcon}
            />
            <h3 className={styles.successTitle}>Billing Profile Created</h3>
            <p className={styles.successText}>
              Your billing profile has been created. Your administrator has been notified and will complete your Stripe setup shortly.
            </p>
            <Button
              variant='contained'
              onClick={handleCreditCardDone}
              className={styles.continueButton}
              data-testid='finish-setup'
            >
              Go to Billing
            </Button>
          </div>
        </div>
      )}

      {/* Step 3b: Inline Bank Form */}
      {step === 'bank_form' && createdProfile && (
        <div className={styles.stepContent}>
          <InlineBankForm
            paymentType={selectedMethod}
            billingCustomerId={createdProfile.billing_customer_id}
            onSaved={onComplete}
          />
        </div>
      )}
    </div>
  )
}

BillingSetupWizard.propTypes = {
  userOrganization: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired
}

export default BillingSetupWizard
